// Helpers
import {
  to,
  deleteAllCookies,
  deleteLocalStorageTokens,
} from '../../lib/helpers';
import { publicRoutes } from '../../routes/RoutesPublic';
import { gaActions } from '../gaActionTypes';
import { selectSignInParamValue } from './authSelectors';
import {
  retryOnException,
  cancelCallAfterTimeout,
} from '../../lib/helpers/util';

// Requests
import {
  logoutRequest,
  checkForSignInAccess,
  getTokenRequest,
  getSDKKeyRequest,
  authorizeRequest,
} from '../../backend/authRequests';

// Actions
import { getUser } from '../user/getUser/getUserActions';
import { clearUser } from '../user/clearUser/clearUserActions';
import { replace } from 'connected-react-router';
import { closeCurrentModal } from '../modal/modalActions';
import { resetOnboardingState } from '../onboarding/onboardingActions';
import { SESSION_ENDED } from '../session/sessionActions';
import { SERVER_ERROR } from '../error/errorActions';
import { clearAllMessageBanners } from '../messageBanner/messageBannerActions';
import { clearAgencyData } from '../agency/agencyActions';
import CONSTANTS from '../../lib/data/constants';

// Consts
export const AUTH_STORE_REDIRECT_LOCATION =
  '[AUTH] Store Login Redirect Location';
export const AUTH_GETTING_SESSION = '[AUTH] Getting Session';
export const AUTH_STORE_SESSION = '[AUTH] Store Session';
export const AUTH_CLEAR_SESSION = `[AUTH] ${gaActions.session} Clear Session`;
export const AUTH_STORE_SIGNIN_PARAMVALUE = '[AUTH] Store Sign-In Param Value';
export const AUTH_CLEAR_SIGNIN_PARAMVALUE = '[AUTH] Clear Sign-In Param Value';
export const AUTH_STORE_IS_PERMITTED_TO_AUTHENTICATE_VIA_IDOLOGY =
  '[AUTH] Store Is Permitted to Authenticate Via Idology';
export const AUTH_STORING_DEVICE_ID = '[AUTH] Storing Device Id';
export const AUTH_STORE_SOCURE_SDK_KEYS = '[AUTH] Store Socure SDK Keys';

//DEPRECATED
// export const storeRedirectLocation = redirectLocation => dispatch =>
//   dispatch({ type: AUTH_STORE_REDIRECT_LOCATION, payload: redirectLocation });

export const checkSSOSession = () => async dispatch => {
  // Check if we already have a token in localStorage
  const access_token = localStorage.getItem('access_token');
  if (!access_token) {
    dispatch({ type: AUTH_GETTING_SESSION });
    const [authorizeResponse, authorizeErr] = await to(dispatch(authorizeRequest()));
    if (!authorizeErr && authorizeResponse?.body?.access_token) {
      dispatch(storeSession(authorizeResponse.body));
      await dispatch(getSession());
    } else {
      dispatch({ type: AUTH_CLEAR_SESSION });
    }
  } else {
    await dispatch(getSession());
  }
};

export const getSession = () => async dispatch => {
  const [_, err] = await to(dispatch(getUser()));
  if (err) {
    dispatch({ type: AUTH_CLEAR_SESSION });
  } else if (localStorage.getItem('access_token')) {
    dispatch({ type: AUTH_STORE_SESSION });
  }
};

export const getSDKKeys = () => async dispatch => {
  const [tokenResponse, tokenError] = await to(dispatch(getTokenRequest()));

  if (tokenError) {
    console.error('Error fetching token for SDK keys', tokenError);
    return Promise.reject(tokenError);
  }
  const { token, uniqueIdentifier } = tokenResponse.body;

  const [keysResponse, keysError] = await to(
    dispatch(getSDKKeyRequest(token, uniqueIdentifier))
  );

  if (keysError) {
    console.error('Error fetching SDK keys', keysError);
    return Promise.reject(keysError);
  }

  const { socureKeys } = keysResponse.body;
  const parsedKeys = JSON.parse(socureKeys);

  // STRY0279978 - Extract OOS-specific key for Device Risk
  const oosKey = parsedKeys.find(
    key => key.name === CONSTANTS.SOCURE_OOS_KEY_NAME
  );

  return dispatch({
    type: AUTH_STORE_SOCURE_SDK_KEYS,
    payload: {
      socureKeys: parsedKeys,
      oosKey: oosKey ? oosKey.value : null,
    },
  });
};

export const getDeviceId = (publicKey = null) => async dispatch => {
  try {
    if (!window.SigmaDeviceManager) {
      throw new Error('SigmaDeviceManager is not defined');
    }

    // If a specific public key is provided (for OOS flow), use it
    // Otherwise, use the default Device Risk SDK initialization
    const fetchSessionId = cancelCallAfterTimeout(
      () => {
        if (publicKey) {
          // Initialize Device Risk with specific key for OOS
          return window.SigmaDeviceManager.getDeviceSessionId({
            publicKey: publicKey,
          });
        }
        return window.SigmaDeviceManager.getDeviceSessionId();
      },
      2000,
      'Socure sessionId request timed out'
    );

    const sessionId = await retryOnException(fetchSessionId);
    return dispatch({
      type: AUTH_STORING_DEVICE_ID,
      payload: sessionId,
    });
  } catch (error) {
    dispatch({ type: SERVER_ERROR, payload: {} });
    console.error(error);
    return Promise.reject(error);
  }
};

//STRY0279978 - OOS-specific Device Risk initialization
export const getOOSDeviceId = () => async (dispatch, getState) => {
  try {
    // Get the OOS key from Redux store
    const { oosKey } = getState().auth;

    if (!oosKey) {
      console.error('OOS Socure key not found');
      return Promise.reject(new Error('OOS key not available'));
    }

    // Call getDeviceId with the OOS-specific key
    return await dispatch(getDeviceId(oosKey));
  } catch (error) {
    console.error('Error getting OOS device ID:', error);
    return Promise.reject(error);
  }
};

export const storeSession = action => dispatch => {
  localStorage.setItem('access_token', action.access_token);
  localStorage.setItem('refresh_token', action.refresh_token);
  localStorage.setItem('session_token', action.session_token);
  dispatch({ type: AUTH_STORE_SESSION });
};

export const clearSession = () => (dispatch, getState) =>
  getState().auth.isAuthenticated
    ? dispatch({ type: AUTH_CLEAR_SESSION })
    : null;

export const redirectToSignIn = () => (dispatch, getState) => {
  deleteLocalStorageTokens();
  deleteAllCookies();
  dispatch(clearSession());
  dispatch(clearUser());

  let { pathname } = getState().router.location;

  if (!publicRoutes.includes(pathname)) dispatch(replace('/'));
};

export const verifyAllowSignIn = paramValue => async dispatch => {
  const [{ body }, err] = await to(dispatch(checkForSignInAccess(paramValue)));
  if (err || body.status === 'ERROR') {
    return Promise.reject();
  } else {
    let validSignInParam = body.data.key;
    let isPermittedToAuthenticateViaIdology = body.data.kbVerificationStatus;
    dispatch({ type: AUTH_STORE_SIGNIN_PARAMVALUE, payload: validSignInParam });
    dispatch({
      type: AUTH_STORE_IS_PERMITTED_TO_AUTHENTICATE_VIA_IDOLOGY,
      payload: isPermittedToAuthenticateViaIdology,
    });
    return Promise.resolve(validSignInParam);
  }
};

export const storeWelcomeParam = paramValue => dispatch => {
  return dispatch({ type: AUTH_STORE_SIGNIN_PARAMVALUE, payload: paramValue });
};

export const clearWelcomeParam = () => dispatch => {
  return dispatch({ type: AUTH_CLEAR_SIGNIN_PARAMVALUE });
};

export const signOut = () => async (dispatch, getState) => {
  const signInParam = selectSignInParamValue(getState());
  await dispatch(logoutRequest());
  deleteLocalStorageTokens();
  deleteAllCookies();
  dispatch(clearSession());
  dispatch(closeCurrentModal());
  dispatch(resetOnboardingState());
  dispatch(clearUser());
  dispatch(clearAllMessageBanners());
  dispatch(clearAgencyData());
  dispatch({ type: SESSION_ENDED });
  dispatch(replace(`/sign-in${signInParam ? `?welcome=${signInParam}` : ''}`));
};

// These sign-out functions, allow for a multi-step signout (such as when a user changes their email)
export const signOutWithoutRedirect = () => async dispatch => {
  await dispatch(logoutRequest());
  deleteLocalStorageTokens();
  deleteAllCookies();
  dispatch(resetOnboardingState());
};

// Note: this does not sign the user out from the Forgerock session, so only use it with signOutWithoutRedirect
export const signOutWithoutAPICall = () => (dispatch, getState) => {
  const signInParam = selectSignInParamValue(getState());
  deleteLocalStorageTokens();
  deleteAllCookies();
  dispatch(clearSession());
  dispatch(closeCurrentModal());
  dispatch(resetOnboardingState());
  dispatch(clearUser());
  dispatch(replace(`/sign-in${signInParam ? `?welcome=${signInParam}` : ''}`));
};
